import { Box, SxProps } from "@mui/system";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import type {
  AdaptiveTextConfig,
  ButtonCurrentValProps,
} from "../../../util/types";
import CommonLoader from "../CommonLoader/inde";
import Hints from "../Hints";
import { imageTextWrapper, loaderWrapper, textStyle } from "./styles";
import PreviewImageWrapper from "../PreviewImageWrapper";
import CustomTooltip from "../CustomTooltip";

type CommonImageCompProps = {
  currentVal: ButtonCurrentValProps;
  setOverRideSelect?: React.Dispatch<React.SetStateAction<boolean>>;
  sx?: SxProps;
  isSelected?: boolean;
  isCheckbox?: boolean;
  disabled?: boolean;
  imageTestid?: string;
  textTestId?: string;
  onLoad?: () => void;
  onSettled?: () => void;
  imageStyle?: SxProps;
  layout?: "stacked" | "inline";
  imagePosition?: "before" | "after";
  disablePreviewWrapper?: boolean;
  onClick?: () => void;
  sourceCanvas?: HTMLCanvasElement;
  adaptiveText?: AdaptiveTextConfig;
  keepLabelWhileLoading?: boolean;
  fillContainer?: boolean;
};

const CommonImageComp: FC<CommonImageCompProps> = ({
  currentVal,
  setOverRideSelect,
  sx,
  isSelected,
  isCheckbox,
  disabled,
  imageTestid,
  textTestId,
  onLoad,
  onSettled,
  imageStyle,
  layout = "stacked",
  imagePosition = "after",
  disablePreviewWrapper,
  onClick,
  sourceCanvas,
  adaptiveText,
  keepLabelWhileLoading = false,
  fillContainer = false,
}) => {
  const [imageState, setImageState] = useState<"loading" | "ready" | "error">(
    currentVal.image ? "loading" : "ready",
  );
  const [isHintHovered, setIsHintHovered] = useState(false);
  const hintRef = useRef<HTMLDivElement | null>(null);
  const previousImageRef = useRef(currentVal.image);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onLoadRef = useRef(onLoad);
  const onSettledRef = useRef(onSettled);
  onLoadRef.current = onLoad;
  onSettledRef.current = onSettled;
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;

  const { image, text, display_text, alt_text } = currentVal;
  const resolvedImageSrc = image;
  const displayedImageState =
    previousImageRef.current === image ? imageState : "loading";
  const loadingImage = displayedImageState === "loading";

  useEffect(() => {
    if (previousImageRef.current !== image) {
      previousImageRef.current = image;
      setImageState(image ? "loading" : "ready");
    }
  }, [image]);

  // Handle load/error events with stable callbacks
  const handleLoad = useCallback(() => {
    setImageState("ready");
    /* c8 ignore next 1 */
    onLoadRef.current?.();
    onSettledRef.current?.();
  }, []);
  /* c8 ignore next 3 */
  const handleError = useCallback(() => {
    setImageState("error");
    onSettledRef.current?.();
  }, []);
  const handleHintEnter = useCallback(() => {
    const hint = hintRef.current;
    if (!hint) {
      setIsHintHovered(false);
      return;
    }
    const overflowsWidth = hint.scrollWidth > hint.clientWidth + 1;
    const overflowsHeight = hint.scrollHeight > hint.clientHeight + 1;
    setIsHintHovered(overflowsWidth || overflowsHeight);
  }, []);
  const handleHintLeave = useCallback(() => {
    setIsHintHovered(false);
  }, []);

  useEffect(() => {
    if (!canvasRef.current || !resolvedImageSrc) return;

    let isCancelled = false;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) {
      handleError();
      return;
    }

    if (sourceCanvas) {
      /* c8 ignore next start */
      if (sourceCanvas.width === 0 || sourceCanvas.height === 0) {
        handleError();
        return;
      }
      /* c8 ignore next end */
      canvas.width = sourceCanvas.width;
      canvas.height = sourceCanvas.height;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(sourceCanvas, 0, 0);

      requestAnimationFrame(() => {
        /* c8 ignore next start */
        if (!isCancelled) {
          handleLoad();
        }
        /* c8 ignore next end */
      });
      return () => {
        isCancelled = true;
      };
    }

    const loadImage = (src: string) =>
      new Promise<HTMLImageElement>((resolve, reject) => {
        const loadedImage = new Image();
        loadedImage.onload = () => {
          resolve(loadedImage);
        };
        loadedImage.onerror = () => {
          reject(new Error("image failed to load"));
        };
        loadedImage.src = src;
      });

    const drawImageOnCanvas = async () => {
      try {
        const loadedImage = await loadImage(resolvedImageSrc);
        // decode() can reject in some browsers/CDN combinations even when the image is loadable.
        // Keep the draw path resilient and proceed with the loaded image.
        if (typeof loadedImage.decode === "function") {
          try {
            await loadedImage.decode();
          } catch {
            // no-op: onload already confirmed image data is available
          }
        }

        /* c8 ignore next 1 */
        if (isCancelled) return;
        canvas.width = loadedImage.naturalWidth || loadedImage.width;
        canvas.height = loadedImage.naturalHeight || loadedImage.height;
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.drawImage(loadedImage, 0, 0);

        requestAnimationFrame(() => {
          if (!isCancelled) {
            handleLoad();
          }
        });
      } catch {
        /* c8 ignore start */
        if (isCancelled) return;
        handleError();
        /* c8 ignore end */
      }
    };

    drawImageOnCanvas();

    return () => {
      isCancelled = true;
    };
  }, [handleError, handleLoad, resolvedImageSrc, sourceCanvas]);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const renderHintText = useCallback(() => {
    if (!display_text) return null;

    return (
      <Box
        ref={hintRef}
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          minWidth: 0,
          width: adaptiveText ? "100%" : undefined,
          flex: isCheckbox || layout === "inline" ? 1 : undefined,
          overflowWrap: adaptiveText ? "normal" : "anywhere",
          wordBreak: adaptiveText ? "normal" : "break-word",
        }}
        {...(isIOS
          ? {
              onTouchStart: handleHintEnter,
              onTouchEnd: handleHintLeave,
            }
          : {
              onMouseEnter: handleHintEnter,
              onMouseLeave: handleHintLeave,
            })}
      >
        <CustomTooltip
          title={isHintHovered ? currentVal?.text : undefined}
          placement="top-start"
          disableInteractive
        >
          <Hints
            text={text}
            testId={textTestId ?? `common-text-${text}`}
            variant="label"
            sx={textStyle(theme, isSelected, disabled, sx, isCheckbox)}
            onClick={onClick}
            adaptiveText={adaptiveText}
          />
        </CustomTooltip>
      </Box>
    );
  }, [
    currentVal?.text,
    adaptiveText,
    disabled,
    display_text,
    handleHintEnter,
    handleHintLeave,
    isCheckbox,
    isHintHovered,
    isSelected,
    layout,
    theme,
    sx,
    text,
  ]);

  // Early return for text-only case (no image)
  if (!image) {
    return renderHintText();
  }

  const renderedImageBox = (
    <Box
      sx={{
        ...imageStyle,
        ...(fillContainer
          ? {
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }
          : {}),
      }}
    >
      <Box
        role="img"
        aria-label={alt_text || text || theme.copy.labels.image}
        component="canvas"
        ref={canvasRef}
        data-testid={imageTestid ?? "common-image"}
        data-image-state={displayedImageState}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        sx={{
          ...imageStyle,
          ...(fillContainer
            ? {
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "100%",
                objectFit: "contain",
              }
            : {}),
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
        }}
      />
    </Box>
  );
  // Image case
  const baseWrapperStyle = imageTextWrapper(
    theme,
    isCheckbox,
    loadingImage,
    layout,
    imagePosition,
  );
  const imageContent = (
    <Box
      sx={{
        opacity: loadingImage ? 0 : 1,
        position: loadingImage ? "absolute" : "static",
        inset: loadingImage ? 0 : "auto",
        overflow: loadingImage ? "hidden" : "visible",
        pointerEvents: loadingImage ? "none" : "auto",
        ...(fillContainer
          ? {
              width: "100%",
              height: "100%",
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }
          : {}),
      }}
    >
      {resolvedImageSrc &&
        (disablePreviewWrapper ? (
          renderedImageBox
        ) : (
          <PreviewImageWrapper
            imageToPreview={image as string}
            altText={alt_text || text}
            ImageBox={renderedImageBox}
            setOverRideSelect={setOverRideSelect}
            sourceCanvasRef={canvasRef}
            fillContainer={fillContainer}
          />
        ))}
    </Box>
  );
  const hintContent =
    (!loadingImage || layout === "inline" || keepLabelWhileLoading) &&
    renderHintText();

  return (
    <Box
      sx={{
        ...baseWrapperStyle,
        ...(fillContainer
          ? { width: "100%", height: "100%", minHeight: 0 }
          : {}),
      }}
    >
      {loadingImage && (
        <Box sx={loaderWrapper}>
          <CommonLoader />
        </Box>
      )}
      {imagePosition === "before" && imageContent}
      {hintContent}
      {imagePosition === "after" && imageContent}
    </Box>
  );
};

export default CommonImageComp;
